import {ECpmCLocationTypeApiEnum, ECpmCLocationVectorTypeApiEnum} from '../../enums/c-customer/cpmc-location.api-enums';

/**
 * Utils for class CpmCLocationApiModel
 */
export class CpmCLocationApiUtils {
    /**
     * Get parent type for a given location type
     * @param locationType In location type
     * @returns Type of parent or undefined
     */
    public static getParentType(locationType?: ECpmCLocationTypeApiEnum): ECpmCLocationTypeApiEnum | undefined {
        switch (locationType) {
            case ECpmCLocationTypeApiEnum.REGION:
                return ECpmCLocationTypeApiEnum.REGION;

            case ECpmCLocationTypeApiEnum.SITE:
                return ECpmCLocationTypeApiEnum.REGION;

            case ECpmCLocationTypeApiEnum.BUILDING:
                return ECpmCLocationTypeApiEnum.SITE;

            case ECpmCLocationTypeApiEnum.FLOOR:
                return ECpmCLocationTypeApiEnum.BUILDING;

            case ECpmCLocationTypeApiEnum.ROOM:
                return ECpmCLocationTypeApiEnum.FLOOR;

            case ECpmCLocationTypeApiEnum.RACK:
                return ECpmCLocationTypeApiEnum.ROOM;
        }

        return undefined;
    }
    /**
     * Get allowed children type for a given locationType
     * @param locationType In location type
     * @returns List of allowed children type
     */
    public static getAllowedChildrenType(locationType?: ECpmCLocationTypeApiEnum): ECpmCLocationTypeApiEnum[] {
        switch (locationType) {
            case ECpmCLocationTypeApiEnum.REGION:
                return [ECpmCLocationTypeApiEnum.REGION, ECpmCLocationTypeApiEnum.SITE];

            case ECpmCLocationTypeApiEnum.SITE:
                return [ECpmCLocationTypeApiEnum.BUILDING];

            case ECpmCLocationTypeApiEnum.BUILDING:
                return [ECpmCLocationTypeApiEnum.FLOOR];

            case ECpmCLocationTypeApiEnum.FLOOR:
                return [ECpmCLocationTypeApiEnum.ROOM];

            case ECpmCLocationTypeApiEnum.ROOM:
                return [ECpmCLocationTypeApiEnum.RACK];
        }

        return [];
    }
    /**
     * Get vector type for vector1 for a given locationType
     * @param locationType In location type
     * @returns ECPMLocationVectorTypeApiEnum for vector1
     */
    public static getVector1Type(locationType?: ECpmCLocationTypeApiEnum): ECpmCLocationVectorTypeApiEnum {
        switch (locationType) {
            case ECpmCLocationTypeApiEnum.ROOM:
            case ECpmCLocationTypeApiEnum.RACK:
                return ECpmCLocationVectorTypeApiEnum.POS_TOP_DOWN;

            case ECpmCLocationTypeApiEnum.REGION:
            case ECpmCLocationTypeApiEnum.SITE:
            case ECpmCLocationTypeApiEnum.BUILDING:
                return ECpmCLocationVectorTypeApiEnum.GPS;
        }

        return ECpmCLocationVectorTypeApiEnum.NONE;
    }
    /**
     * Get vector type for vector2 for a given locationType
     * @param locationType In location type
     * @returns ECPMLocationVectorTypeApiEnum for vector2
     */
    public static getVector2Type(locationType?: ECpmCLocationTypeApiEnum): ECpmCLocationVectorTypeApiEnum {
        switch (locationType) {
            case ECpmCLocationTypeApiEnum.BUILDING:
            case ECpmCLocationTypeApiEnum.FLOOR:
            case ECpmCLocationTypeApiEnum.ROOM:
            case ECpmCLocationTypeApiEnum.RACK:
                return ECpmCLocationVectorTypeApiEnum.SIZE_3D;

            case ECpmCLocationTypeApiEnum.REGION:
                return ECpmCLocationVectorTypeApiEnum.GPS;
        }

        return ECpmCLocationVectorTypeApiEnum.NONE;
    }
    /**
     * Get the component specifications for a given vector type
     * @param vectorType In vector type
     * @param locationType Type of location for additional informations
     * @param vectorIndex Index of the vector for additional informations
     * @returns Vector component specifications
     */
    public static getVectorTypeComponentSpec(
        vectorType: ECpmCLocationVectorTypeApiEnum,
        locationType?: ECpmCLocationTypeApiEnum,
        vectorIndex?: number
    ): {
        hint: string;
        components: {enabled: boolean; hint: string; unitHint: string; dmin: number; dmax: number}[];
    } {
        switch (vectorType) {
            case ECpmCLocationVectorTypeApiEnum.POS_TOP_DOWN:
                return {
                    hint: 'Position',
                    components: [
                        {enabled: true, hint: 'Position X (left to right)', unitHint: 'm', dmin: 3, dmax: 3},
                        {enabled: true, hint: 'Position Y (bottom to top)', unitHint: 'm', dmin: 3, dmax: 3},
                        {enabled: false, hint: '', unitHint: '', dmin: 0, dmax: 0}
                    ]
                };
            case ECpmCLocationVectorTypeApiEnum.POS_3D:
                return {
                    hint: 'Position',
                    components: [
                        {enabled: true, hint: 'Position X (left to right)', unitHint: 'm', dmin: 3, dmax: 3},
                        {enabled: true, hint: 'Position Y (bottom to top)', unitHint: 'm', dmin: 3, dmax: 3},
                        {enabled: true, hint: 'Position Z (floor to ceilling)', unitHint: 'm', dmin: 3, dmax: 3}
                    ]
                };
            case ECpmCLocationVectorTypeApiEnum.SIZE_3D:
                return {
                    hint: 'Size',
                    components: [
                        {enabled: true, hint: 'Width X (left to right)', unitHint: 'm', dmin: 3, dmax: 3},
                        {enabled: true, hint: 'Depth Y (bottom to top)', unitHint: 'm', dmin: 3, dmax: 3},
                        {enabled: true, hint: 'Height Z (floor to ceilling)', unitHint: 'm', dmin: 3, dmax: 3}
                    ]
                };
            case ECpmCLocationVectorTypeApiEnum.GPS: {
                let hint = 'GPS Coordinates';
                if (locationType === ECpmCLocationTypeApiEnum.REGION && vectorIndex) {
                    hint = (vectorIndex === 1 ? 'North west ' : 'South east ') + hint;
                }

                return {
                    hint: hint,
                    components: [
                        {enabled: true, hint: 'Latitude', unitHint: 'Decimal°', dmin: 6, dmax: 6},
                        {enabled: true, hint: 'Longitude', unitHint: 'Decimal°', dmin: 6, dmax: 6},
                        {enabled: false, hint: '', unitHint: '', dmin: 0, dmax: 0}
                    ]
                };
            }
        }

        return {
            hint: '',
            components: [
                {enabled: false, hint: '', unitHint: '', dmin: 0, dmax: 0},
                {enabled: false, hint: '', unitHint: '', dmin: 0, dmax: 0},
                {enabled: false, hint: '', unitHint: '', dmin: 0, dmax: 0}
            ]
        };
    }
    /**
     * Has a given location type a slot count
     * @param locationType In location type
     * @returns True or false
     */
    public static hasSlotCount(locationType?: ECpmCLocationTypeApiEnum): boolean {
        return locationType === ECpmCLocationTypeApiEnum.RACK;
    }
}
